/**
 * Single source of truth for "when does this client renew" and "how many
 * clients renew in the next N days" (DEV-326).
 *
 * The Dashboard card, /renewals, the Reports renewal pipeline and the
 * Calendar suggestion each used to compute this their own way (UTC vs local
 * parsing, window starting today vs the 1st of the month, reading a
 * `client.renewalDate` field that doesn't exist), so the same book showed
 * 19 / 17 / 0. Renewal dates live on client_plans (falling back to the plan
 * row); a client's next renewal is the earliest of those on or after today.
 *
 * Dates are treated as calendar days: "2026-10-19" and
 * "2026-10-19T00:00:00.000Z" both mean Oct 19 in every timezone, built as
 * LOCAL midnight so they compare correctly against the browser's "today".
 */

type DateLike = string | Date | null | undefined;

export interface RenewalDateFields {
  renewalDate?: DateLike;
  endDate?: DateLike;
}

export interface ClientPlanRenewalInput extends RenewalDateFields {
  clientId: number;
  planId?: number | null;
  plan?: RenewalDateFields | null;
}

export interface RenewalWindowOptions {
  /** Defaults to now; only its local calendar day matters. */
  today?: Date;
  /** When given, client plans for clients outside this set are ignored. */
  clientIds?: ReadonlySet<number>;
  /** Library-plan fallback when the client plan's joined `plan` has no date. */
  getPlan?: (planId: number) => RenewalDateFields | null | undefined;
}

/** Parses a date-only value as LOCAL midnight of that calendar day. */
export function parseDateOnly(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const s = String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Whole calendar days from `today` to `date` (negative = past). DST-safe. */
export function calendarDaysUntil(date: Date, today: Date = new Date()): number {
  const a = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const b = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((a - b) / 86_400_000);
}

/** One client plan's renewal date: its own, then the joined plan's, then the library plan's. */
export function resolvePlanRenewalDate(
  cp: ClientPlanRenewalInput,
  getPlan?: RenewalWindowOptions["getPlan"],
): Date | null {
  const fallback = cp.planId != null && getPlan ? getPlan(cp.planId) : null;
  for (const v of [
    cp.renewalDate,
    cp.endDate,
    cp.plan?.renewalDate,
    cp.plan?.endDate,
    fallback?.renewalDate,
    fallback?.endDate,
  ]) {
    const d = parseDateOnly(v);
    if (d) return d;
  }
  return null;
}

/** clientId -> earliest renewal date on or after today. Clients with none are absent. */
export function resolveNextRenewalByClient(
  clientPlans: readonly ClientPlanRenewalInput[],
  opts: RenewalWindowOptions = {},
): Map<number, Date> {
  const today = opts.today ?? new Date();
  const next = new Map<number, Date>();
  for (const cp of clientPlans) {
    if (opts.clientIds && !opts.clientIds.has(cp.clientId)) continue;
    const d = resolvePlanRenewalDate(cp, opts.getPlan);
    if (!d || calendarDaysUntil(d, today) < 0) continue;
    const prev = next.get(cp.clientId);
    if (!prev || d < prev) next.set(cp.clientId, d);
  }
  return next;
}

/** Distinct clients whose next renewal is within `days` calendar days (today inclusive). */
export function countClientsRenewingWithin(
  clientPlans: readonly ClientPlanRenewalInput[],
  days: number,
  opts: RenewalWindowOptions = {},
): number {
  const today = opts.today ?? new Date();
  let count = 0;
  resolveNextRenewalByClient(clientPlans, { ...opts, today }).forEach(d => {
    if (calendarDaysUntil(d, today) <= days) count++;
  });
  return count;
}

export const RENEWAL_PIPELINE_BUCKETS = ["0-30 days", "31-60 days", "61-90 days", "90+ days"] as const;

/** Index into RENEWAL_PIPELINE_BUCKETS for a non-negative days-until value. */
export function renewalPipelineBucket(daysUntil: number): number {
  if (daysUntil <= 30) return 0;
  if (daysUntil <= 60) return 1;
  if (daysUntil <= 90) return 2;
  return 3;
}
